/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { HotelRecentSearch, RoomConfig } from '../types';

export interface HotelSearchHistoryDestination {
  id: string;
  name: string;
  type: string;
  fullName: string;
  aa_id: string;
  country_code: string;
  city_id: string | null;
  isRecentSearches: true;
}

export interface HotelSearchHistoryEntry {
  destination: HotelSearchHistoryDestination;
  date: {
    checkin: string;
    checkout: string;
  };
  rooms: Array<{
    adult: number;
    child: number;
    infant: number;
  }>;
  createdAt: string;
}

export interface HotelSearchHistoryDestinationInput {
  id?: string;
  aa_id?: string;
  name?: string;
  full_name?: string;
  type?: string;
  country_code?: string;
  city_id?: string | null;
}

type SearchHistoryRoomInput = RoomConfig | HotelRecentSearch['rooms'][number];

const isRoomConfigInput = (room: SearchHistoryRoomInput): room is RoomConfig =>
  'adults' in room;

const mapSearchHistoryRoom = (room: SearchHistoryRoomInput) => {
  if (isRoomConfigInput(room)) {
    return {
      adult: room.adults,
      child: room.children?.length ?? 0,
      infant: room.infants ?? 0,
    };
  }

  return {
    adult: room.adult,
    child: room.child,
    infant: room.infant,
  };
};

export const buildHotelSearchHistoryEntry = ({
  destination,
  checkin,
  checkout,
  rooms = [],
}: {
  destination?: HotelSearchHistoryDestinationInput | null;
  checkin?: string;
  checkout?: string;
  rooms?: SearchHistoryRoomInput[];
}): HotelSearchHistoryEntry | null => {
  const destinationId = destination?.id?.trim();
  const checkinDate = checkin?.trim();
  const checkoutDate = checkout?.trim();

  if (!destination || !destinationId || !checkinDate || !checkoutDate) {
    return null;
  }

  return {
    destination: {
      id: destinationId,
      name: destination.name ?? '',
      type: destination.type ?? '',
      fullName: destination.full_name ?? '',
      aa_id: destination.aa_id ?? '',
      country_code: destination.country_code ?? '',
      city_id: destination.city_id ?? null,
      isRecentSearches: true,
    },
    date: {
      checkin: checkinDate,
      checkout: checkoutDate,
    },
    rooms: rooms.map(mapSearchHistoryRoom),
    createdAt: new Date().toISOString(),
  };
};

/**
 * Flattens the hotel recent-searches persist cache into the list shape the
 * native bridge expects (`NativeModules.ReactToNative.sendSearchHistory`),
 * matching flights' `flattenRecentSearchesForHistory`: send the whole stored
 * list on every search, not just the one just submitted.
 */
export const flattenHotelRecentSearchesForHistory = (
  searches?: HotelRecentSearch[],
): HotelSearchHistoryEntry[] => {
  if (!searches?.length) {
    return [];
  }

  return searches.flatMap((search) => {
    const entry = buildHotelSearchHistoryEntry({
      destination: search.destination,
      checkin: search.date.checkin,
      checkout: search.date.checkout,
      rooms: search.rooms,
    });

    return entry ? [entry] : [];
  });
};
